import { Box, GridProps, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import ContentSection, {
  SectionHeading,
} from '@/components/frontend/ContentSection';
import { ListingProps } from '@/types/listing';
import formatCurrency from '@/lib/formatCurrency';
import { urls } from '@/lib/urls';
import ButtonLink from '../ButtonLink';
import Link from '../Link';

const ListingBuyerCosts = ({
  listing,
  ...gridStyles
}: Pick<ListingProps, 'listing'> & GridProps) => {
  const { t } = useTranslation('backoffice');
  const { locale = 'fi' } = useRouter();

  const totalPrice = formatCurrency(listing?.total_price as number, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return (
    <ContentSection {...gridStyles}>
      <SectionHeading>{t('price_information', 'Hintatiedot')}</SectionHeading>
      <SimpleGrid columns={1} spacingX={5} spacingY={7}>
        <Heading size="sm">
          {t('frontoffice:listing_price', 'Kohteen hinta')}
        </Heading>
        <Text>
          {listing.sales_assignment?.price_type === 'minimum'
            ? t('offer_price_description_minimum', {
                totalPrice,
                defaultValue:
                  'Kohteen lähtöhinta on {{ totalPrice }}, mutta voit tarjota myös enemmän. Voit tarjota vain kokonaisia euroja.',
              })
            : t('offer_price_description_request', {
                totalPrice,
                defaultValue:
                  'Kohteen hintapyyntö on {{ totalPrice }}, voit tarjota enemmän tai vähemmän.',
              })}
        </Text>

        {listing.additional_costs_for_buyer?.trim() ? (
          <>
            <Heading size="sm">
              {t(
                'backoffice:additional_costs_for_buyer',
                'Ostajalle tulevat maksut',
              )}
            </Heading>
            <Text whiteSpace="pre-line">
              {listing.additional_costs_for_buyer}
            </Text>
          </>
        ) : null}

        <Heading size="sm">
          {t('frontoffice:offer_disclaimer_title', 'Kaupantekoajankohta')}
        </Heading>
        <Trans t={t} i18nKey="frontoffice:offer_disclaimer">
          <Text whiteSpace="pre-line">
            Ostaja sitoutuu myyjän myyntiehdon mukaisesti siihen, että
            kiinteistökauppa tulee tehdä 6 viikon kuluessa hyväksytystä
            ostotarjouksesta. Jos kiinteistökaupan solmiminen kuitenkin
            viivästyy ostajan pankista johtuvasta syystä enemmän kuin 8 viikkoa,
            on myyjällä oikeus peräytyä korvauksetta kaupasta. Katso myös{' '}
            <Link
              href={
                {
                  sv: '/regler-for-anbudsforfarande',
                  en: '/rules-for-the-tender-procedure',
                }[locale] || '/tarjousmenettelyn-saannot'
              }
              target="_blank"
              color="highlightColor"
            >
              tarjousmenettelyn säännöt
            </Link>
            .
          </Text>
        </Trans>

        <Box>
          <ButtonLink
            link={{
              url: urls.public.listing.offer(listing.public_id ?? ''),
              title: t('make_offer', 'Tee tarjous'),
            }}
            boxShadow="md"
            size="lg"
            colorScheme="primary"
          />
        </Box>
      </SimpleGrid>
    </ContentSection>
  );
};

export default ListingBuyerCosts;
